import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { getCrmApiBaseUrl } from "../../utils/utils";
import "../../wallet.css";
import { Pill } from "./walletShared";

// Hoisted to module scope (not defined inside RegularTaskManager) — a component defined inside
// another component's render body gets a new identity every render, which makes React unmount
// and remount its instances (and any input inside loses focus) on every keystroke.
const Field = ({ label: lbl, children, hint, required }) => (
  <div className="w-field" style={{ flex: "1 1 220px" }}>
    <label className="w-field-label">{lbl} {required && <span style={{ color: "#dc2626" }}>*</span>}</label>
    {children}
    {hint && <div className="w-help-text" style={{ marginTop: 4 }}>{hint}</div>}
  </div>
);

const SectionHeading = ({ step, title, desc }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
    {step && (
      <span
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#e6f7fd",
          color: "#0089c7",
          fontSize: 12,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {step}
      </span>
    )}
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{title}</div>
      {desc && <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 2 }}>{desc}</div>}
    </div>
  </div>
);

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: state.isFocused ? "#00a6ed" : "#e7eaf0",
    boxShadow: "none",
    "&:hover": { borderColor: "#00a6ed" },
  }),
};

// ------------------------------------------------------------------ bulk import helpers
// (module scope — pure functions, no component state)

const str = (v) => (v === null || v === undefined ? "" : String(v).trim());
/** Loose text match key: "Al  Ain Labs " and "al ain labs" compare equal. */
const norm = (v) => str(v).toLowerCase().replace(/\s+/g, " ");
/** Loose header key: "Homebase Id ( Drop off)", "homebaseId" and "HOMEBASE_ID" all collapse alike. */
const normKey = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");

/** Read a cell by any of its accepted header spellings (headers are matched loosely). */
const pick = (row, keys) => {
  const n = row?.n || {};
  for (const k of keys) {
    const v = n[normKey(k)];
    if (v !== undefined && str(v) !== "") return str(v);
  }
  return "";
};

/**
 * Excel time cells arrive as decimals (0.5 = 12:00), "7:00 AM", or "07:00" — normalise them all
 * to the HH:mm the API expects. Returns null when the value can't be read as a time.
 */
const toHHmm = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim().toUpperCase();

  const m24 = s.match(/^(\d{1,2}):(\d{2})(:\d{2})?$/);
  if (m24) {
    const h = parseInt(m24[1], 10);
    if (h >= 0 && h <= 23) return `${String(h).padStart(2, "0")}:${m24[2]}`;
    return null;
  }

  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (m12) {
    let h = parseInt(m12[1], 10);
    if (m12[3] === "PM" && h !== 12) h += 12;
    if (m12[3] === "AM" && h === 12) h = 0;
    if (h >= 0 && h <= 23) return `${String(h).padStart(2, "0")}:${m12[2]}`;
    return null;
  }

  const dec = parseFloat(s);
  if (!isNaN(dec) && dec >= 0 && dec < 1) {
    const mins = Math.round(dec * 24 * 60);
    return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  }
  return null;
};

/** First number in a cell. "TRIP-001" -> 1 (the dash is a separator, not a minus sign). */
const toNum = (v) => {
  const s = str(v);
  if (s === "") return null;
  const m = s.match(/-?\d+(?:\.\d+)?/);
  if (!m) return null;
  let token = m[0];
  if (token.startsWith("-") && m.index > 0 && !/[\s(]$/.test(s.slice(0, m.index))) token = token.slice(1);
  const n = Number(token);
  return isNaN(n) ? null : n;
};

const toInt = (v) => {
  const n = toNum(v);
  return n === null ? null : Math.trunc(n);
};

/** Only a cell that is *entirely* digits may be read as an id — "Lab 24" is a name, not id 24. */
const asId = (v) => (/^\d+$/.test(str(v)) ? Number(str(v)) : null);

/**
 * Columns that exist in the medical/normal order sheets but mean nothing to a Regular Task:
 * the task runs EVERY day (so any date range is irrelevant) and the backend mints its own
 * reference per daily order (RT{id}-{yyyyMMdd}-{rand}).
 */
const IGNORED_COLUMNS = ["Start Date", "End Date", "Order Date", "Date", "booking_date", "Reference", "Ops Reference"];
const IGNORED_KEYS = new Set(IGNORED_COLUMNS.map(normKey));

/** Excel serials / DD-MM-YYYY etc. only matter for display here — dates are dropped anyway. */
const normalizeCell = (key, value) => {
  const k = String(key).toLowerCase();
  if (k.includes("time") && !k.includes("timezone")) {
    const t = toHHmm(value);
    return t !== null ? t : value;
  }
  if (k.includes("date") && typeof value === "number") {
    const d = XLSX.SSF.parse_date_code(value);
    if (d) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.y}-${pad(d.m)}-${pad(d.d)}`;
    }
  }
  return value;
};

/**
 * The order sheets use "pickup" / "delivery" for a single leg; a regular medical trip normally
 * wants both, which is what the manual form defaults to. Returns null for anything unrecognised.
 */
const normalizeTaskType = (value) => {
  const v = norm(value).replace(/[^a-z]/g, "");
  if (!v) return "pickup_delivery";
  if (v === "pickup" || v === "pick" || v === "pickuponly") return "pickup";
  if (v === "delivery" || v === "drop" || v === "dropoff" || v === "deliveryonly") return "delivery";
  if (v === "both" || (v.includes("pickup") && v.includes("deliver"))) return "pickup_delivery";
  return null;
};

/**
 * Accepted header spellings per field. The production medical sheet keeps driver NAMES in a
 * column headed "Driver Id", so both spellings are treated as "name or id" — exactly how the
 * backend's bulk mapper reads them.
 */
const KEYS = {
  partner: ["Partner Name", "partnerName", "Partner"],
  partnerId: ["Partner Id", "partnerId", "pid"],
  driver: ["Driver Name", "driverName", "Driver", "Driver Id", "driverId"],
  trip: ["Trip Number", "tripNumber", "Trip"],
  taskType: ["Task Type", "taskType"],
  team: ["Team Id", "teamId", "Team"],
  homebase: ["Homebase Id ( Drop off)", "Homebase Id", "homebaseId", "Drop Homebase", "Homebase"],
  location: ["Location Id ( Pickup)", "Location Id", "locationId", "Pickup Location", "Location"],
  senderCity: ["Sender City", "sCity", "Pickup City"],
  consigneeCity: ["Consignee City", "conCity", "Delivery City"],
  dropStart: ["Drop Start Time", "dropStartTime"],
  dropEnd: ["Drop End Time", "dropEndTime"],
  runTime: ["Run Time", "Create Order At", "Run At"],
  name: ["Task Name", "Name", "Trip Name"],
  pickupName: ["Pickup Name", "Sender Name", "pickupName"],
  pickupPhone: ["Pickup Phone", "Sender Phone", "pickupPhone"],
  pickupAddress: ["Pickup Address", "Sender Address", "pickupAddress"],
  pickupCity: ["Pickup City", "Sender City", "pickupCity"],
  receiverName: ["Receiver Name", "Receiver's Name", "receiverName"],
  receiverPhone: ["Receiver Phone", "Receiver's Mobile Number", "receiverPhone"],
  deliveryAddress: ["Delivery Address", "Delivery Adress", "deliveryAddress"],
  deliveryDetail: ["Apt Details", "Apt", "deliveryAddressDetail"],
  city: ["City", "Delivery City", "deliveryCity"],
  itemDesc: ["Item Description", "itemDesc"],
  cod: ["Amount to be Recieved (AED)", "Amount to be Received (AED)", "COD Amount", "codAmount"],
  weight: ["KG Weight", "Weight", "weightKg"],
  noItems: ["noOfitems", "No Of Items", "noItems"],
  slot: ["Slot", "slot"],
};

/** What each column becomes, for the "columns detected" panel. */
const KEY_LABELS = {
  partner: "Partner", partnerId: "Partner id", driver: "Driver", trip: "Trip #", taskType: "Task type",
  team: "Team Id", homebase: "Homebase (drop)", location: "Location (pickup)", senderCity: "Sender city",
  consigneeCity: "Consignee city", dropStart: "Drop start", dropEnd: "Drop end", runTime: "Run time",
  name: "Task name", pickupName: "Pickup name", pickupPhone: "Pickup phone", pickupAddress: "Pickup address",
  pickupCity: "Pickup city", receiverName: "Receiver name", receiverPhone: "Receiver phone",
  deliveryAddress: "Delivery address", deliveryDetail: "Apt details", city: "City",
  itemDesc: "Item description", cod: "COD amount", weight: "Weight", noItems: "No. of items", slot: "Slot",
};

const TASK_TYPE_OPTIONS = [
  { value: "pickup_delivery", label: "Pickup & Delivery" },
  { value: "pickup", label: "Pickup" },
  { value: "delivery", label: "Delivery" },
];

/** Fields offered by the "set for every row" bulk editor in step 3. */
const BULK_FIELDS_MEDICAL = [
  { value: "runTime", label: "Run time", type: "time" },
  { value: "tripNumber", label: "Trip #", type: "number" },
  { value: "taskType", label: "Task type", type: "select" },
  { value: "teamId", label: "Team Id", type: "text" },
  { value: "dropHomebaseId", label: "Homebase (drop)", type: "text", list: "rt-homebase-list" },
  { value: "pickupLocationId", label: "Location (pickup)", type: "text", list: "rt-location-list" },
  { value: "pickupCity", label: "Sender city", type: "text" },
  { value: "deliveryCity", label: "Consignee city", type: "text" },
  { value: "dropStartTime", label: "Drop start", type: "time" },
  { value: "dropEndTime", label: "Drop end", type: "time" },
];

const BULK_FIELDS_NORMAL = [
  { value: "runTime", label: "Run time", type: "time" },
  { value: "tripNumber", label: "Trip #", type: "number" },
  { value: "deliveryCity", label: "City", type: "text" },
  { value: "pickupCity", label: "Pickup city", type: "text" },
  { value: "itemDesc", label: "Item description", type: "text" },
  { value: "codAmount", label: "COD amount", type: "text" },
  { value: "slot", label: "Slot", type: "number" },
];

const cellStyle = (invalid) => ({
  width: "100%",
  minHeight: 30,
  padding: "4px 6px",
  fontSize: 12.5,
  borderColor: invalid ? "#dc2626" : undefined,
  background: invalid ? "#fff5f5" : undefined,
});

/**
 * Grid cell input. Uncontrolled, committing on blur/Enter — typing in one cell of an 80-row grid
 * must not re-render the whole grid on every keystroke. The key lets it pick up values changed
 * from outside (bulk apply, driver mapping).
 */
const CellInput = ({ value, onCommit, type = "text", list, invalid, placeholder }) => (
  <input
    key={String(value ?? "")}
    type={type}
    list={list}
    placeholder={placeholder}
    defaultValue={value ?? ""}
    className="w-input"
    style={cellStyle(invalid)}
    onBlur={(e) => {
      if (e.target.value !== String(value ?? "")) onCommit(e.target.value);
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter") e.target.blur();
    }}
  />
);

const CellSelect = ({ value, onChange, options, invalid }) => (
  <select className="w-input" style={cellStyle(invalid)} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">—</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

/**
 * Expanded row — the fields that need a searchable dropdown or more room than a grid cell.
 * Rendered only for the open row, which is why a full driver/partner list is affordable here.
 */
const ImportRowEditor = ({ data, isMedical, partnerOptions, driverOptions, onEdit }) => {
  const r = data;
  const text = (field, lbl, placeholder) => (
    <Field label={lbl}>
      <CellInput value={r.payload[field] ?? ""} invalid={r.errorFields.has(field)} onCommit={(v) => onEdit(r.idx, field, v)} placeholder={placeholder} />
    </Field>
  );
  return (
    <div style={{ padding: "14px 18px", background: "#fafbfc", borderBottom: "1px solid #e7eaf0" }}>
      <div className="w-form-row">
        <div className="w-field" style={{ flex: "1 1 260px" }}>
          <label className="w-field-label">Partner</label>
          <Select
            options={partnerOptions}
            value={partnerOptions.find((o) => o.value === r.payload.partnerId) || null}
            onChange={(opt) => onEdit(r.idx, "partnerId", opt ? opt.value : null)}
            placeholder="Select partner…"
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles}
          />
        </div>
        {isMedical && (
          <div className="w-field" style={{ flex: "1 1 260px" }}>
            <label className="w-field-label">
              Driver {r.sheetDriver && <span style={{ fontWeight: 400, color: "#6b7280" }}>· file says “{r.sheetDriver}”</span>}
            </label>
            <Select
              options={driverOptions}
              value={driverOptions.find((o) => o.value === r.payload.driverId) || null}
              onChange={(opt) => onEdit(r.idx, "driverId", opt ? opt.value : "")}
              placeholder="Search driver…"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectStyles}
              noOptionsMessage={() => "No drivers found"}
            />
          </div>
        )}
        {isMedical ? (
          <>
            {text("pickupName", "Pickup name")}
            {text("pickupPhone", "Pickup phone")}
            {text("pickupAddress", "Pickup address")}
            {text("receiverName", "Receiver name")}
            {text("receiverPhone", "Receiver phone")}
            {text("deliveryAddress", "Delivery address")}
          </>
        ) : (
          <>
            {text("deliveryAddressDetail", "Apt / details")}
            {text("itemDesc", "Item description")}
            {text("slot", "Slot")}
            {text("pickupName", "Pickup name")}
            {text("pickupPhone", "Pickup phone")}
            {text("pickupAddress", "Pickup address")}
          </>
        )}
      </div>
    </div>
  );
};

/** Wizard header for the import screen. */
const Stepper = ({ step, steps, onGo }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "0 0 16px" }}>
    {steps.map((s, i) => {
      const num = i + 1;
      const state = num === step ? "current" : num < step ? "done" : "todo";
      const reachable = s.reachable !== false && num !== step;
      return (
        <button
          key={s.title}
          onClick={() => reachable && onGo(num)}
          disabled={!reachable}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            borderRadius: 999,
            border: `1px solid ${state === "current" ? "#00a6ed" : "#e7eaf0"}`,
            background: state === "current" ? "#e6f7fd" : "#fff",
            color: state === "todo" ? "#9ca3af" : "#111827",
            cursor: reachable ? "pointer" : "default",
            fontSize: 12.5,
            fontWeight: state === "current" ? 700 : 500,
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              fontSize: 11,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: state === "done" ? "#16a34a" : state === "current" ? "#00a6ed" : "#e7eaf0",
              color: state === "todo" ? "#6b7280" : "#fff",
            }}
          >
            {state === "done" ? "✓" : num}
          </span>
          {s.title}
        </button>
      );
    })}
  </div>
);

const MEDICAL_TEMPLATE_HEADERS = [
  "Partner Name", "Driver Name", "Trip Number", "Task Type", "Team Id",
  "Homebase Id ( Drop off)", "Location Id ( Pickup)", "Sender City", "Consignee City",
  "Drop Start Time", "Drop End Time",
  // optional extras
  "Task Name", "Run Time", "Pickup Name", "Pickup Phone", "Pickup Address",
  "Receiver Name", "Receiver Phone", "Delivery Address",
  // present in the medical order sheet — accepted and ignored
  "Start Date", "End Date", "Reference",
];

const NORMAL_TEMPLATE_HEADERS = [
  "Partner Name", "Receiver's Name", "Receiver's Mobile Number", "Delivery Adress",
  "Apt Details", "City", "Item Description", "Amount to be Recieved (AED)",
  "KG Weight", "noOfitems", "Slot",
  // optional extras
  "Task Name", "Run Time", "Pickup Name", "Pickup Phone", "Pickup Address", "Pickup City",
  // present in the normal order sheet — accepted and ignored
  "Ops Reference", "booking_date",
];

const MEDICAL_SAMPLE = {
  "Partner Name": "Sample Medical Partner",
  "Driver Name": "Sample Driver",
  "Trip Number": 1,
  "Task Type": "pickup_delivery",
  "Team Id": "1",
  "Homebase Id ( Drop off)": "HB001",
  "Location Id ( Pickup)": "12",
  "Sender City": "Dubai",
  "Consignee City": "Abu Dhabi",
  "Drop Start Time": "07:00",
  "Drop End Time": "17:30",
  "Task Name": "",
  "Run Time": "00:00",
  "Pickup Name": "",
  "Pickup Phone": "",
  "Pickup Address": "",
  "Receiver Name": "",
  "Receiver Phone": "",
  "Delivery Address": "",
  "Start Date": "2026-01-01",
  "End Date": "2026-01-31",
  "Reference": "",
};

const NORMAL_SAMPLE = {
  "Partner Name": "Sample Partner",
  "Receiver's Name": "John Doe",
  "Receiver's Mobile Number": "+971500000000",
  "Delivery Adress": "Building 12, Al Barsha",
  "Apt Details": "Flat 401",
  "City": "Dubai",
  "Item Description": "Documents",
  "Amount to be Recieved (AED)": 0,
  "KG Weight": 1,
  "noOfitems": 1,
  "Slot": 1,
  "Task Name": "",
  "Run Time": "00:00",
  "Pickup Name": "",
  "Pickup Phone": "",
  "Pickup Address": "",
  "Pickup City": "Dubai",
  "Ops Reference": "",
  "booking_date": "2026-01-01",
};

/**
 * Regular Task manager (driver frontend). A Regular Task is a saved "trip" template that the
 * driver-app scheduler turns into a real order every day at its run time.
 *   variant = NORMAL  -> gen order  (createGenOrdersViaShypv)
 *   variant = MEDICAL -> med order  (createBulkOrdersViaShypv)
 * Fields mirror the manual New Normal / New Medical task forms; the from-date/to-date range is
 * intentionally dropped (it runs daily). CRUD: {REACT_APP_API_URL}shypvdriverapp/regular-tasks
 */
/**
 * Small iOS-style on/off switch for the enable/disable state. Clicks are stopped from
 * bubbling so it can sit inside a clickable card row without also opening the detail view.
 */
function ToggleSwitch({ checked, onChange, disabled = false, showLabel = true, title }) {
  const on = !!checked;
  return (
    <label
      title={title || (on ? "Enabled — click to disable" : "Disabled — click to enable")}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "relative", display: "inline-flex", alignItems: "center", gap: 8,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1, userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={on}
        disabled={disabled}
        onChange={(e) => { e.stopPropagation(); if (!disabled) onChange(e.target.checked); }}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      <span
        aria-hidden="true"
        style={{
          width: 38, height: 21, borderRadius: 999, flexShrink: 0, display: "inline-block",
          background: on ? "#16a34a" : "#cbd5e1", position: "relative",
          transition: "background .18s ease", boxShadow: "inset 0 1px 2px rgba(0,0,0,.08)",
        }}
      >
        <span
          style={{
            position: "absolute", top: 2, left: on ? 19 : 2, width: 17, height: 17,
            borderRadius: "50%", background: "#fff", transition: "left .18s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,.25)",
          }}
        />
      </span>
      {showLabel && (
        <span style={{ fontSize: 12, fontWeight: 600, color: on ? "#16a34a" : "#6b7280" }}>
          {on ? "Enabled" : "Disabled"}
        </span>
      )}
    </label>
  );
}

export default function RegularTaskManager({ variant = "NORMAL" }) {
  const isMedical = variant === "MEDICAL";
  const label = isMedical ? "Medical" : "Normal";

  const API_URL = process.env.REACT_APP_API_URL; // driver API, trailing slash
  const RT_API = `${API_URL}shypvdriverapp/regular-tasks`;

  const emptyForm = useMemo(
    () => ({
      id: null,
      name: "",
      tripNumber: "",
      partner: null,
      runTime: "00:00",
      timezone: "Asia/Dubai",
      enabled: true,
      // pickup
      pickupName: "",
      pickupPhone: "",
      pickupAddress: "",
      pickupCity: "",
      pickupLocationId: "",
      // drop / receiver
      receiverName: "",
      receiverPhone: "",
      deliveryAddress: "",
      deliveryAddressDetail: "",
      deliveryCity: "",
      dropHomebaseId: "",
      // medical assignment
      driverId: "",
      // Medical orders always create both a pickup and a delivery leg, so
      // default the type to the combined option.
      taskType: "pickup_delivery",
      teamId: "",
      dropStartTime: "",
      dropEndTime: "",
      // normal package
      itemDesc: "",
      weightKg: "",
      noItems: "",
      codAmount: "",
      slot: "",
    }),
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // ---- list search / filter / bulk selection ----
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");      // ALL | ENABLED | DISABLED
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectionToggle, setClearSelectionToggle] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [partners, setPartners] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [homebases, setHomebases] = useState([]);
  const [banner, setBanner] = useState(null);
  // "list" (table/cards of tasks) | "form" (create/edit) | "detail" (one task + its run history)
  const [mode, setMode] = useState("list");
  const [viewMode, setViewMode] = useState("table"); // "table" | "card", for the list
  const [form, setForm] = useState(emptyForm);
  const [nameEdited, setNameEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // ---- bulk import (Excel) ----
  // Wizard: 1 upload -> 2 partner & schedule -> 3 fix & review -> 4 create.
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  // Parsed sheet rows: { data: <cells as typed in the file>, n: <loose-header lookup> }
  const [importParsed, setImportParsed] = useState([]);
  const [importDefaults, setImportDefaults] = useState({
    runTime: "00:00",
    timezone: "Asia/Dubai",
    enabled: true,
    skipDuplicates: true,
  });
  // The production sheet has no partner column — one partner is chosen here for the whole file
  // (a Partner Name column, when present, wins per row).
  const [importPartner, setImportPartner] = useState(null);
  // Fixes made on screen: { rowIndex: { field: value } }. Applied on top of the sheet values, so
  // an edit survives changing the defaults and re-validates immediately.
  const [rowEdits, setRowEdits] = useState({});
  const [excludedRows, setExcludedRows] = useState(() => new Set());
  const [importWarehouses, setImportWarehouses] = useState([]); // pickup locations of importPartner
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [importResults, setImportResults] = useState(null);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  // "set for every row" bulk editor (step 3)
  const [bulkField, setBulkField] = useState("runTime");
  const [bulkValue, setBulkValue] = useState("");

  const flash = (type, text) => {
    setBanner({ type, text });
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setBanner(null), 6000);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // ------------------------------------------------------------------ loaders
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${RT_API}?variant=${variant}`);
      setRows(res.data?.data || []);
    } catch (err) {
      flash("error", "Failed to load tasks: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [RT_API, variant]);

  const fetchPartners = useCallback(async () => {
    try {
      const base = getCrmApiBaseUrl();
      const res = await axios.get(`${base}/v1.0/partner/retrieve_list?page=0`);
      const all = res.data?.data || [];
      // Medical trips may only be assigned to medical partners (same filter as the manual
      // New Medical Task form).
      const filtered = isMedical ? all.filter((p) => p.accountType?.toLowerCase() === "medical") : all;
      setPartners(filtered);
    } catch (err) {
      console.warn("Could not load partners:", err.message);
    }
  }, [isMedical]);

  const fetchDrivers = useCallback(async () => {
    if (!isMedical) return;
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/driver/getAllDrivers`);
      const data = res.data?.data || [];
      setDrivers(data.map((d) => ({ driver_id: d.driverId, driver_name: d.name })));
    } catch (err) {
      console.warn("Could not load drivers:", err.message);
    }
  }, [API_URL, isMedical]);

  // Registered pickup locations for a partner (partner-scoped, so re-fetched on partner change).
  const fetchWarehouses = useCallback(
    async (partnerId) => {
      if (!isMedical || !partnerId) {
        setWarehouses([]);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}shypvdriverapp/partner-warehouse?partnerId=${partnerId}`);
        setWarehouses(res.data?.success ? res.data.data || [] : []);
      } catch (err) {
        console.warn("Could not load pickup locations:", err.message);
        setWarehouses([]);
      }
    },
    [API_URL, isMedical]
  );

  // Registered drop-off homebases (not partner-scoped, loaded once).
  const fetchHomebases = useCallback(async () => {
    if (!isMedical) return;
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/homebase/getHomebases`);
      setHomebases(res.data?.data || []);
    } catch (err) {
      console.warn("Could not load homebases:", err.message);
    }
  }, [API_URL, isMedical]);

  // Run history (orders created) for one task — loaded when its detail view opens.
  const fetchRuns = useCallback(
    async (configId) => {
      setRunsLoading(true);
      try {
        const res = await axios.get(`${RT_API}/${configId}/runs`);
        setRuns(res.data?.data || []);
      } catch (err) {
        console.warn("Could not load run history:", err.message);
        setRuns([]);
      } finally {
        setRunsLoading(false);
      }
    },
    [RT_API]
  );

  useEffect(() => {
    fetchTasks();
    fetchPartners();
    fetchDrivers();
    fetchHomebases();
  }, [fetchTasks, fetchPartners, fetchDrivers, fetchHomebases]);

  const selectedPartnerId = form.partner ? form.partner.value : null;
  useEffect(() => {
    fetchWarehouses(selectedPartnerId);
  }, [selectedPartnerId, fetchWarehouses]);

  const partnerOptions = useMemo(
    () =>
      [...partners]
        .sort((a, b) => (a.pname || "").localeCompare(b.pname || ""))
        .map((p) => ({ value: p.pid, label: `${String(p.pid).padStart(2, "0")} - ${p.pname}`, pname: p.pname })),
    [partners]
  );

  // Required-and-can-grow-long lists get a searchable react-select dropdown (same treatment
  // as Partner); short fixed enums stay as a native <select>.
  const driverOptions = useMemo(
    () => drivers.map((d) => ({ value: String(d.driver_id), label: `${d.driver_id} - ${d.driver_name}` })),
    [drivers]
  );
  // Compare as strings — the native <select> this replaces coerced values to strings for
  // matching, and the drivers API / saved row don't always agree on number vs string.
  const selectedDriver = driverOptions.find((o) => o.value === String(form.driverId)) || null;
  const onDriverChange = (opt) => setForm((f) => ({ ...f, driverId: opt ? opt.value : "" }));

  // ------------------------------------------------------------------ trip-name suggestion
  const suggestName = (partner, trip) => (partner ? `${partner.pname} - Trip ${trip || 1}` : "");

  const onPartnerChange = (opt) => {
    // next trip number for this partner = how many tasks it already has + 1
    const nextTrip = opt ? rows.filter((r) => r.partnerId === opt.value).length + 1 : 1;
    setForm((f) => {
      const trip = f.id ? f.tripNumber : nextTrip;
      // pickupLocationId belongs to the previous partner's warehouse list — clear it so a
      // stale/invalid selection can't be submitted.
      const samePartner = f.partner && opt && f.partner.value === opt.value;
      return {
        ...f,
        partner: opt,
        tripNumber: trip,
        pickupLocationId: samePartner ? f.pickupLocationId : "",
        name: nameEdited ? f.name : suggestName(opt, trip),
      };
    });
  };

  const onTripChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, tripNumber: v, name: nameEdited ? f.name : suggestName(f.partner, v) }));
  };

  const onNameChange = (e) => {
    setNameEdited(true);
    setForm((f) => ({ ...f, name: e.target.value }));
  };

  // ------------------------------------------------------------------ form open / reset
  const openCreate = () => {
    setForm({ ...emptyForm });
    setNameEdited(false);
    setMode("form");
  };

  const openEdit = (row) => {
    setForm({
      ...emptyForm,
      ...Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v == null ? "" : v])),
      runTime: (row.runTime || "00:00").slice(0, 5),
      tripNumber: row.tripNumber ?? "",
      partner: row.partnerId != null ? { value: row.partnerId, label: row.partnerName || `#${row.partnerId}`, pname: row.partnerName } : null,
      enabled: row.enabled !== false,
    });
    setNameEdited(true); // keep the saved name; don't auto-overwrite while editing
    setMode("form");
  };

  const resetAll = () => {
    setForm({ ...emptyForm });
    setNameEdited(false);
  };

  const closeForm = () => {
    setMode("list");
    setForm(emptyForm);
    setNameEdited(false);
  };

  // ------------------------------------------------------------------ detail (run history)
  const openDetail = (row) => {
    setDetailRow(row);
    setMode("detail");
    fetchRuns(row.id);
  };

  const closeDetail = () => {
    setMode("list");
    setDetailRow(null);
    setRuns([]);
  };

  // ------------------------------------------------------------------ save
  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

  const buildPayload = () => ({
    variant,
    name: form.name.trim(),
    tripNumber: numOrNull(form.tripNumber),
    partnerId: form.partner ? form.partner.value : null,
    partnerName: form.partner ? form.partner.pname : null,
    partnerCity: form.pickupCity || null,
    runTime: form.runTime || "00:00",
    timezone: form.timezone || "Asia/Dubai",
    enabled: form.enabled !== false,
    // pickup
    pickupName: form.pickupName || null,
    pickupPhone: form.pickupPhone || null,
    pickupAddress: form.pickupAddress || null,
    pickupCity: form.pickupCity || null,
    pickupLocationId: form.pickupLocationId || null,
    // drop / receiver
    receiverName: form.receiverName || null,
    receiverPhone: form.receiverPhone || null,
    deliveryAddress: form.deliveryAddress || null,
    deliveryAddressDetail: form.deliveryAddressDetail || null,
    deliveryCity: form.deliveryCity || null,
    dropHomebaseId: form.dropHomebaseId || null,
    // medical assignment
    driverId: form.driverId || null,
    taskType: form.taskType || null,
    teamId: form.teamId || null,
    dropStartTime: form.dropStartTime || null,
    dropEndTime: form.dropEndTime || null,
    // normal package
    itemDesc: form.itemDesc || null,
    codAmount: form.codAmount || null,
    weightKg: numOrNull(form.weightKg),
    noItems: numOrNull(form.noItems),
    slot: numOrNull(form.slot),
  });

  const save = async () => {
    if (!form.name.trim()) {
      flash("error", "Task name is required");
      return;
    }
    if (!form.partner) {
      flash("error", "Please select a partner");
      return;
    }
    if (isMedical && !form.driverId) {
      flash("error", "Please select a driver");
      return;
    }
    if (isMedical && !form.dropHomebaseId) {
      flash("error", "Please select a drop homebase");
      return;
    }
    if (isMedical && !String(form.teamId).trim()) {
      flash("error", "Team ID is required");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = form.id ? await axios.put(`${RT_API}/${form.id}`, payload) : await axios.post(RT_API, payload);
      if (res.data?.success !== false) {
        flash("success", form.id ? "Task updated — applies to the next scheduled order" : "Task created");
        closeForm();
        fetchTasks();
      } else {
        flash("error", res.data?.message || "Save failed");
      }
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------ row actions
  const toggleEnabled = async (row) => {
    try {
      await axios.patch(`${RT_API}/${row.id}/enabled`, { enabled: !row.enabled });
      fetchTasks();
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    }
  };

  // "Today" as the task's own timezone sees it — matches how the scheduler/backend decide
  // last_run_date, so this lines up with what actually happened rather than the browser's date.
  const isRunToday = (row) => {
    if (!row.lastRunDate) return false;
    try {
      const todayInTz = new Date().toLocaleDateString("en-CA", { timeZone: row.timezone || "Asia/Dubai" });
      return row.lastRunDate === todayInTz;
    } catch {
      return false;
    }
  };

  const createNow = async (row) => {
    const already = isRunToday(row);
    const confirmMsg = already
      ? `An order for "${row.name}" was already created today (last run ${row.lastRunAt || "earlier today"}).\n\nCreate another one anyway?`
      : `Create today's ${label.toLowerCase()} order now for "${row.name}"?\n(Use this if the scheduler missed it.)`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(`${RT_API}/${row.id}/run-now`);
      const msg = res.data?.message || "Triggered";
      flash(String(msg).startsWith("FAILED") ? "error" : "success", msg);
      fetchTasks();
      if (mode === "detail" && detailRow?.id === row.id) fetchRuns(row.id);
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete regular task "${row.name}"? This stops its daily orders.`)) return;
    try {
      await axios.delete(`${RT_API}/${row.id}`);
      flash("success", "Deleted");
      fetchTasks();
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    }
  };

  // ------------------------------------------------------------------ bulk import (Excel)
  // One sheet row = one regular task. Column meanings follow the medical bulk upload
  // (NewMedicalTaskCreation.js + the backend's BulkMedicalOrderMappingService):
  //   • partner / driver cells hold NAMES, matched to ids the same way the backend does —
  //     numeric id, then exact name, then partial name;
  //   • Homebase Id / Location Id are passed through to the order exactly as written
  //     (the backend never resolves those either), so a name like "BMC - MBZ" is valid;
  //   • Start Date / End Date are dropped — a regular task has no date range, it runs daily.

  const openImport = () => {
    setImportStep(1);
    setImportFile(null);
    setImportParsed([]);
    setImportResults(null);
    setRowEdits({});
    setExcludedRows(new Set());
    setImportPartner(null);
    setShowOnlyIssues(false);
    setImportProgress({ done: 0, total: 0 });
    setMode("import");
  };

  const closeImport = () => {
    setMode("list");
    setImportFile(null);
    setImportParsed([]);
    setImportResults(null);
    setRowEdits({});
    setExcludedRows(new Set());
  };

  const downloadTemplate = () => {
    const headers = isMedical ? MEDICAL_TEMPLATE_HEADERS : NORMAL_TEMPLATE_HEADERS;
    const sample = isMedical ? MEDICAL_SAMPLE : NORMAL_SAMPLE;
    const ws = XLSX.utils.json_to_sheet([sample], { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Regular Tasks");
    XLSX.writeFile(wb, `regular_task_${label.toLowerCase()}_template.xlsx`);
  };

  const parseFile = (file) => {
    setImportFile(file);
    setImportResults(null);
    setRowEdits({});
    setExcludedRows(new Set());
    if (!file) {
      setImportParsed([]);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
        const parsed = raw
          .map((row) => {
            const data = {};
            const n = {};
            const entries = Object.entries(row).map(([k, v]) => [k, normalizeCell(k, v)]);
            entries.forEach(([k, value]) => {
              data[k] = value;
              n[normKey(k)] = value;
            });
            // Second pass: also index headers with their "( Drop off)"-style annotation stripped,
            // so "Homebase Id ( Drop off)" is reachable as plain "Homebase Id". Never overwrites a
            // real column of that name.
            entries.forEach(([k, value]) => {
              const bare = normKey(String(k).replace(/\([^)]*\)/g, ""));
              if (bare && n[bare] === undefined) n[bare] = value;
            });
            return { data, n };
          })
          // drop fully blank rows (trailing formatting in Excel produces them)
          .filter((r) => Object.values(r.data).some((v) => str(v) !== ""));
        setImportParsed(parsed);
        setShowOnlyIssues(false);
        if (parsed.length === 0) {
          flash("error", "No data rows found in that file.");
        } else {
          setImportStep(2);
          flash("success", `Read ${parsed.length} row(s) from ${file.name}.`);
        }
      } catch (err) {
        setImportParsed([]);
        flash("error", "Could not read that file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Pickup locations of the chosen partner — same source as the manual form's dropdown; used to
  // suggest / recognise Location Id values.
  useEffect(() => {
    let cancelled = false;
    if (mode !== "import" || !isMedical || !importPartner) {
      setImportWarehouses([]);
      return undefined;
    }
    axios
      .get(`${API_URL}shypvdriverapp/partner-warehouse?partnerId=${importPartner.value}`)
      .then((res) => {
        if (!cancelled) setImportWarehouses(res.data?.success ? res.data.data || [] : []);
      })
      .catch(() => {
        if (!cancelled) setImportWarehouses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, isMedical, importPartner, API_URL]);

  // Which of the ignored columns this file actually contains — shown so the user can see the date
  // range was recognised and dropped on purpose, not misread.
  const ignoredPresent = useMemo(() => {
    if (importParsed.length === 0) return [];
    const present = new Set();
    importParsed.forEach((r) => Object.keys(r.n).forEach((k) => { if (IGNORED_KEYS.has(k)) present.add(k); }));
    return IGNORED_COLUMNS.filter((c) => present.has(normKey(c)));
  }, [importParsed]);

  const sheetHasPartnerColumn = useMemo(
    () => importParsed.some((r) => pick(r, KEYS.partner) !== "" || pick(r, KEYS.partnerId) !== ""),
    [importParsed]
  );

  /** What every column of THIS file was understood as — shown before anything is created. */
  const columnMap = useMemo(() => {
    const headers = [];
    importParsed.forEach((r) =>
      Object.keys(r.data).forEach((h) => { if (!headers.includes(h)) headers.push(h); })
    );
    return headers.map((header) => {
      // match the header as written and with any "( Drop off)" annotation stripped
      const forms = new Set([normKey(header), normKey(header.replace(/\([^)]*\)/g, ""))]);
      if ([...forms].some((f) => IGNORED_KEYS.has(f))) {
        return { header, status: "ignored", target: "ignored", note: "Regular tasks run every day and mint their own reference" };
      }
      const hit = Object.keys(KEYS).find((group) => KEYS[group].some((alias) => forms.has(normKey(alias))));
      if (hit) return { header, status: "mapped", target: KEY_LABELS[hit] || hit, note: "" };
      return { header, status: "unused", target: "not used", note: "Not part of a regular task" };
    });
  }, [importParsed]);

  const bulkFields = isMedical ? BULK_FIELDS_MEDICAL : BULK_FIELDS_NORMAL;
  const bulkFieldDef = bulkFields.find((f) => f.value === bulkField) || bulkFields[0];

  /** Same matching order as the backend: numeric id → exact name → partial name. */
  const matchPartner = useCallback(
    (cell) => {
      const value = str(cell);
      if (!value) return { option: null, how: null, others: 0 };
      const id = asId(value);
      if (id != null) {
        const byId = partnerOptions.find((o) => o.value === id);
        if (byId) return { option: byId, how: "id", others: 0 };
      }
      const target = norm(value);
      const exact = partnerOptions.filter((o) => norm(o.pname) === target);
      if (exact.length) return { option: exact[0], how: "exact", others: exact.length - 1 };
      const partial = partnerOptions.filter(
        (o) => norm(o.pname).includes(target) || target.includes(norm(o.pname))
      );
      if (partial.length) return { option: partial[0], how: "partial", others: partial.length - 1 };
      return { option: null, how: null, others: 0 };
    },
    [partnerOptions]
  );

  const matchDriver = useCallback(
    (cell) => {
      const value = str(cell);
      if (!value) return { option: null, how: null, others: 0 };
      const id = asId(value);
      if (id != null) {
        const byId = driverOptions.find((o) => o.value === String(id));
        if (byId) return { option: byId, how: "id", others: 0 };
      }
      const target = norm(value);
      const optionFor = (d) => driverOptions.find((o) => o.value === String(d.driver_id)) || null;
      const exact = drivers.filter((d) => norm(d.driver_name) === target);
      if (exact.length) return { option: optionFor(exact[0]), how: "exact", others: exact.length - 1 };
      const partial = drivers.filter(
        (d) => norm(d.driver_name).includes(target) || target.includes(norm(d.driver_name))
      );
      if (partial.length) return { option: optionFor(partial[0]), how: "partial", others: partial.length - 1 };
      return { option: null, how: null, others: 0 };
    },
    [drivers, driverOptions]
  );

  /**
   * Sheet row -> the task that will be created. Values come from the sheet, then the step-2
   * defaults, then any on-screen edit (which always wins). Validation runs on the final values,
   * so fixing a cell clears its error immediately.
   */
  const buildRow = useCallback(
    (row, idx, nameCounts) => {
      const errors = [];
      const warnings = [];
      const notes = [];
      const edits = rowEdits[idx] || {};

      // -- partner: sheet column first, otherwise the partner chosen for the file
      const partnerCell = pick(row, KEYS.partner) || pick(row, KEYS.partnerId);
      const partnerMatch = partnerCell ? matchPartner(partnerCell) : { option: null, how: null, others: 0 };
      const partner = partnerMatch.option || importPartner || null;
      if (edits.partnerId === undefined) {
        if (partnerCell && !partnerMatch.option && importPartner) {
          warnings.push(`Partner "${partnerCell}" not found — using ${importPartner.pname}`);
        } else if (partnerMatch.how === "partial") {
          notes.push(`Partner matched by partial name: "${partnerCell}" → ${partnerMatch.option.pname}`);
        }
      }

      // -- driver (medical): the production sheet keeps driver NAMES in a "Driver Id" column
      const driverCell = pick(row, KEYS.driver);
      const driverMatch = isMedical ? matchDriver(driverCell) : { option: null, how: null, others: 0 };
      if (isMedical && edits.driverId === undefined) {
        if (driverCell && driverMatch.how === "partial") {
          notes.push(`Driver matched by partial name: "${driverCell}" → ${driverMatch.option.label}`);
        }
        if (driverMatch.others > 0) {
          warnings.push(`${driverMatch.others + 1} drivers match "${driverCell}" — check the one picked`);
        }
      }

      const tripNumber = toInt(pick(row, KEYS.trip));
      const runTimeCell = pick(row, KEYS.runTime);
      const runTimeFromSheet = toHHmm(runTimeCell);
      if (runTimeCell && !runTimeFromSheet) warnings.push(`Run Time "${runTimeCell}" unreadable — using the default`);

      const homebase = pick(row, KEYS.homebase);
      const location = pick(row, KEYS.location);
      const senderCity = pick(row, KEYS.senderCity);
      const consigneeCity = pick(row, KEYS.consigneeCity) || pick(row, KEYS.city);

      // -- base task, before on-screen edits
      const base = {
        variant,
        partnerId: partner ? partner.value : null,
        partnerName: partner ? partner.pname : null,
        tripNumber: tripNumber == null ? 1 : tripNumber,
        runTime: runTimeFromSheet || importDefaults.runTime,
        timezone: importDefaults.timezone || "Asia/Dubai",
        enabled: importDefaults.enabled !== false,
        pickupName: pick(row, KEYS.pickupName) || null,
        pickupPhone: pick(row, KEYS.pickupPhone) || null,
        pickupAddress: pick(row, KEYS.pickupAddress) || null,
      };

      if (isMedical) {
        Object.assign(base, {
          driverId: driverMatch.option ? driverMatch.option.value : null,
          taskType: normalizeTaskType(pick(row, KEYS.taskType)) || "",
          teamId: pick(row, KEYS.team) || "",
          dropHomebaseId: homebase,
          pickupLocationId: location,
          pickupCity: senderCity,
          deliveryCity: consigneeCity,
          dropStartTime: toHHmm(pick(row, KEYS.dropStart)) || pick(row, KEYS.dropStart),
          dropEndTime: toHHmm(pick(row, KEYS.dropEnd)) || pick(row, KEYS.dropEnd),
          receiverName: pick(row, KEYS.receiverName) || null,
          receiverPhone: pick(row, KEYS.receiverPhone) || null,
          deliveryAddress: pick(row, KEYS.deliveryAddress) || null,
        });
      } else {
        Object.assign(base, {
          receiverName: pick(row, KEYS.receiverName),
          receiverPhone: pick(row, KEYS.receiverPhone),
          deliveryAddress: pick(row, KEYS.deliveryAddress),
          deliveryAddressDetail: pick(row, KEYS.deliveryDetail) || null,
          deliveryCity: pick(row, KEYS.city),
          pickupCity: pick(row, KEYS.pickupCity),
          itemDesc: pick(row, KEYS.itemDesc) || null,
          codAmount: pick(row, KEYS.cod) || null,
          weightKg: toNum(pick(row, KEYS.weight)),
          noItems: toInt(pick(row, KEYS.noItems)),
          slot: toInt(pick(row, KEYS.slot)),
        });
      }

      // -- auto name (editable). Repeats in the same file get " #2", " #3" … so identical rows
      //    stay distinct tasks instead of colliding.
      const driverLabel = edits.driverId
        ? driverOptions.find((o) => o.value === edits.driverId)?.label
        : driverMatch.option?.label;
      const driverNameOnly = driverLabel ? driverLabel.replace(/^\d+\s*-\s*/, "") : driverCell;
      const parts = isMedical
        ? [`Trip ${base.tripNumber}`, edits.pickupLocationId ?? location, driverNameOnly]
        : [partner ? partner.pname : "", `Trip ${base.tripNumber}`, base.receiverName];
      let autoName = parts.filter((p) => str(p) !== "").join(" - ");
      if (!autoName) autoName = `Regular task ${idx + 1}`;
      const seenCount = (nameCounts.get(norm(autoName)) || 0) + 1;
      nameCounts.set(norm(autoName), seenCount);
      if (seenCount > 1) autoName = `${autoName} #${seenCount}`;
      base.name = pick(row, KEYS.name) || autoName;

      // -- on-screen edits win
      const payload = { ...base, ...edits };
      if (payload.partnerId != null) {
        payload.partnerName = partnerOptions.find((o) => o.value === payload.partnerId)?.pname || payload.partnerName;
      }
      payload.partnerCity = payload.pickupCity || null;
      payload.tripNumber = toInt(payload.tripNumber);
      if (!isMedical) {
        payload.weightKg = toNum(payload.weightKg);
        payload.noItems = toInt(payload.noItems);
        payload.slot = toInt(payload.slot);
      }

      // -- validate what will actually be sent; errorFields drives the red outline in the grid
      const errorFields = new Set();
      const fail = (field, message) => { errors.push(message); errorFields.add(field); };
      const need = (field, message) => { if (str(payload[field]) === "") fail(field, message); };
      need("name", "Task name is required");
      if (payload.partnerId == null) {
        fail("partnerId", sheetHasPartnerColumn ? "Partner could not be matched" : "Pick a partner in step 2");
      }
      if (isMedical) {
        if (!payload.driverId) {
          fail("driverId", driverCell ? `Driver "${driverCell}" not found — pick one` : "Driver is required");
        }
        if (!payload.taskType) fail("taskType", "Task Type must be pickup, delivery or pickup_delivery");
        need("teamId", "Team Id is required");
        need("dropHomebaseId", "Homebase Id (Drop off) is required");
        need("pickupCity", "Sender City is required");
        need("deliveryCity", "Consignee City is required");
        if (!toHHmm(payload.dropStartTime)) fail("dropStartTime", "Drop Start Time must be a time like 07:00");
        if (!toHHmm(payload.dropEndTime)) fail("dropEndTime", "Drop End Time must be a time like 17:30");
        if (str(payload.pickupLocationId) === "") {
          warnings.push("No pickup Location Id — the order is created without one");
        } else if (importWarehouses.length > 0 && !importWarehouses.some((w) => norm(w.name) === norm(payload.pickupLocationId) || String(w.id) === str(payload.pickupLocationId))) {
          notes.push(`"${payload.pickupLocationId}" isn't a saved pickup location for this partner — sent as written`);
        }
        if (str(payload.dropHomebaseId) !== "" && homebases.length > 0 &&
            !homebases.some((h) => norm(h.homebaseName) === norm(payload.dropHomebaseId) || norm(h.homebaseCode) === norm(payload.dropHomebaseId))) {
          notes.push(`"${payload.dropHomebaseId}" isn't a registered homebase — sent as written`);
        }
      } else {
        need("receiverName", "Receiver's Name is required");
        need("receiverPhone", "Receiver's Mobile Number is required");
        need("deliveryAddress", "Delivery Address is required");
        need("deliveryCity", "City is required");
      }
      if (!toHHmm(payload.runTime)) fail("runTime", "Run time must be a time like 00:00");

      const clash = rows.some((r) => norm(r.name) === norm(payload.name));
      if (clash) warnings.push("A task with this name already exists");

      return {
        idx,
        raw: row,
        payload,
        errors,
        errorFields,
        warnings,
        notes,
        duplicate: clash,
        excluded: excludedRows.has(idx),
        sheetDriver: driverCell,
        sheetPartner: partnerCell,
        driverHow: driverMatch.how,
        driverEdited: edits.driverId !== undefined,
      };
    },
    [
      variant, isMedical, importDefaults, importPartner, matchPartner, matchDriver, rowEdits,
      excludedRows, driverOptions, partnerOptions, homebases, importWarehouses, rows,
      sheetHasPartnerColumn,
    ]
  );

  // Recomputed on every edit — the grid always shows the current, validated state.
  const importRows = useMemo(() => {
    const nameCounts = new Map();
    return importParsed.map((row, idx) => buildRow(row, idx, nameCounts));
  }, [importParsed, buildRow]);

  const importReady = useMemo(
    () =>
      importRows.filter(
        (r) => !r.excluded && r.errors.length === 0 && !(importDefaults.skipDuplicates && r.duplicate)
      ),
    [importRows, importDefaults.skipDuplicates]
  );
  const importBlocked = importRows.filter((r) => !r.excluded && r.errors.length > 0);
  const importSkipped = importRows.length - importReady.length;

  // ---- on-screen fixes
  const editRow = (idx, field, value) =>
    setRowEdits((m) => ({ ...m, [idx]: { ...m[idx], [field]: value } }));

  /** Set one field on every row (optionally only those matching a predicate on the built row). */
  const editMany = (field, value, predicate) =>
    setRowEdits((m) => {
      const next = { ...m };
      importRows.forEach((r) => {
        if (predicate && !predicate(r)) return;
        next[r.idx] = { ...next[r.idx], [field]: value };
      });
      return next;
    });

  const toggleExcluded = (idx) =>
    setExcludedRows((s) => {
      const n = new Set(s);
      if (n.has(idx)) n.delete(idx);
      else n.add(idx);
      return n;
    });

  const clearEdits = () => {
    setRowEdits({});
    setExcludedRows(new Set());
    flash("success", "On-screen edits cleared — back to the file values.");
  };

  /**
   * Every distinct driver name in the sheet, with what it matched to and how many rows use it —
   * fixing one entry here fixes all of its rows at once (the same idea as the medical bulk
   * upload's name mapping step).
   */
  const driverGroups = useMemo(() => {
    if (!isMedical) return [];
    const map = new Map();
    importRows.forEach((r) => {
      const key = norm(r.sheetDriver) || `__row${r.idx}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          display: r.sheetDriver || `(blank, row ${r.idx + 2})`,
          rows: [],
          driverId: r.payload.driverId || "",
          how: r.driverHow,
          mixed: false,
          edited: false,
        });
      }
      const g = map.get(key);
      g.rows.push(r.idx);
      if ((r.payload.driverId || "") !== g.driverId) g.mixed = true;
      if (r.driverEdited) g.edited = true;
    });
    return [...map.values()].sort((a, b) => a.display.localeCompare(b.display));
  }, [importRows, isMedical]);

  const partnerGroups = useMemo(() => {
    if (!sheetHasPartnerColumn) return [];
    const map = new Map();
    importRows.forEach((r) => {
      const key = norm(r.sheetPartner);
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { key, display: r.sheetPartner, rows: [], partnerId: r.payload.partnerId ?? "" });
      }
      map.get(key).rows.push(r.idx);
    });
    return [...map.values()].sort((a, b) => a.display.localeCompare(b.display));
  }, [importRows, sheetHasPartnerColumn]);

  const submitImport = async () => {
    if (importReady.length === 0) {
      flash("error", "Nothing to create — fix the rows flagged in step 3 first.");
      return;
    }
    if (
      !window.confirm(
        `Create ${importReady.length} ${label.toLowerCase()} regular task(s)?\n\n` +
          "Each one creates a new order EVERY day at its run time until you disable it." +
          (importSkipped > 0 ? `\n\n${importSkipped} row(s) will be skipped.` : "")
      )
    )
      return;

    setImporting(true);
    setImportResults(null);
    setImportProgress({ done: 0, total: importReady.length });
    const created = [];
    const failed = [];

    // Sequential on purpose: predictable creation order, and it doesn't hammer the create
    // endpoint with a whole sheet at once.
    for (let i = 0; i < importReady.length; i++) {
      const row = importReady[i];
      try {
        const res = await axios.post(RT_API, row.payload);
        if (res.data?.success === false) failed.push({ ...row, error: res.data?.message || "Create failed" });
        else created.push({ ...row, id: res.data?.data?.id });
      } catch (err) {
        failed.push({ ...row, error: err.response?.data?.message || err.message });
      }
      setImportProgress({ done: i + 1, total: importReady.length });
    }

    setImporting(false);
    setImportResults({ created, failed, skipped: importSkipped });
    fetchTasks();
    if (failed.length === 0) flash("success", `Created ${created.length} regular task(s).`);
    else flash("error", `${created.length} created, ${failed.length} failed — see the results below.`);
  };

  /** Failed rows exported exactly as they came in, plus the reason — fix and re-upload. */
  const downloadFailedRows = () => {
    if (!importResults?.failed?.length) return;
    const data = importResults.failed.map((r) => ({ ...r.raw.data, "Import Error": r.error }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Failed");
    XLSX.writeFile(wb, `regular_task_import_failed_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const downloadInvalidRows = () => {
    if (importBlocked.length === 0) return;
    const data = importBlocked.map((r) => ({ ...r.raw.data, "Issues": r.errors.join("; ") }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Needs fixing");
    XLSX.writeFile(wb, `regular_task_import_invalid_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // ------------------------------------------------------------------ columns
  /**
   * Tokenised search: the query is split on whitespace and EVERY token must match somewhere
   * in the row (AND across tokens, OR across fields). That makes it narrow progressively —
   * "adil trip 2" finds Adil's trip 2 — instead of the usual "one substring in one column".
   * Driver is matched by name too, not just the raw id stored on the task.
   */
  const rowHaystack = useCallback(
    (r) => {
      const driverLabel = driverOptions.find((o) => o.value === String(r.driverId))?.label || "";
      return [
        r.name, r.partnerName, r.partnerId, r.tripNumber, r.runTime, r.timezone,
        r.driverId, driverLabel, r.teamId, r.taskType,
        r.pickupName, r.pickupCity, r.pickupLocationId,
        r.receiverName, r.deliveryCity, r.deliveryAddress, r.dropHomebaseId,
        r.lastRunStatus, r.lastRunAt, r.lastRunDate,
        r.enabled ? "enabled active" : "disabled inactive paused",
      ]
        .map((v) => (v == null ? "" : String(v).toLowerCase()))
        .join(" ");
    },
    [driverOptions]
  );

  const visibleRows = useMemo(() => {
    let out = rows;
    if (statusFilter !== "ALL") out = out.filter((r) => (statusFilter === "ENABLED" ? !!r.enabled : !r.enabled));
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return out;
    return out.filter((r) => {
      const hay = rowHaystack(r);
      return tokens.every((t) => hay.includes(t));
    });
  }, [rows, search, statusFilter, rowHaystack]);

  /** Apply an action to every selected trip, reporting how many succeeded/failed. */
  const runBulk = async (action) => {
    if (!selectedRows.length) return;
    const verb = { enable: "Enable", disable: "Disable", run: "Create today's order for", delete: "Delete" }[action];
    const n = selectedRows.length;
    if (!window.confirm(`${verb} ${n} selected ${label.toLowerCase()} trip${n > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    let ok = 0;
    const failures = [];
    for (const row of selectedRows) {
      try {
        if (action === "enable" || action === "disable") {
          await axios.patch(`${RT_API}/${row.id}/enabled`, { enabled: action === "enable" });
        } else if (action === "run") {
          await axios.post(`${RT_API}/${row.id}/run-now`);
        } else if (action === "delete") {
          await axios.delete(`${RT_API}/${row.id}`);
        }
        ok++;
      } catch (err) {
        failures.push(`${row.name}: ${err.response?.data?.message || err.message}`);
      }
    }
    setBulkBusy(false);
    setSelectedRows([]);
    setClearSelectionToggle((t) => !t);
    flash(
      failures.length ? "error" : "success",
      `${verb}: ${ok} succeeded${failures.length ? `, ${failures.length} failed — ${failures[0]}` : ""}`
    );
    fetchTasks();
  };

  const columns = [
    {
      name: "Task",
      cell: (r) => (
        <button
          onClick={() => openDetail(r)}
          style={{ background: "none", border: "none", padding: 0, color: "#00a6ed", fontWeight: 600, cursor: "pointer", textAlign: "left" }}
          title="View orders created by this task"
        >
          {r.name}
        </button>
      ),
      selector: (r) => r.name,
      sortable: true,
      grow: 2,
    },
    { name: "Partner", selector: (r) => r.partnerName || (r.partnerId ? `#${r.partnerId}` : "—"), sortable: true, grow: 1.5 },
    {
      name: "Trip",
      selector: (r) => r.tripNumber ?? "—",
      width: "80px",
      center: true,
      sortable: true,
      // numeric, so trip 10 sorts after trip 9 (a plain string sort would not)
      sortFunction: (a, b) => (Number(a.tripNumber) || 0) - (Number(b.tripNumber) || 0),
    },
    {
      name: "Runs at",
      selector: (r) => `${(r.runTime || "00:00").slice(0, 5)}`,
      width: "100px",
      sortable: true,
      sortFunction: (a, b) => String(a.runTime || "").localeCompare(String(b.runTime || "")),
    },
    {
      name: "Status",
      cell: (r) => (
        <ToggleSwitch checked={r.enabled} onChange={() => toggleEnabled(r)} />
      ),
      width: "140px",
      sortable: true,
      sortFunction: (a, b) => Number(!!b.enabled) - Number(!!a.enabled),
    },
    {
      name: "Last run",
      cell: (r) =>
        r.lastRunAt ? (
          <span title={r.lastRunStatus || ""} style={{ fontSize: 12 }}>
            {r.lastRunAt}
            {r.lastRunStatus && (
              <span style={{ color: String(r.lastRunStatus).startsWith("FAILED") ? "#dc2626" : "#16a34a" }}>
                {" "}· {String(r.lastRunStatus).startsWith("FAILED") ? "failed" : "ok"}
              </span>
            )}
          </span>
        ) : (
          "—"
        ),
      grow: 1.4,
      sortable: true,
      // never-run rows sort last regardless of direction-agnostic string compare
      sortFunction: (a, b) => String(a.lastRunAt || "").localeCompare(String(b.lastRunAt || "")),
    },
    {
      name: "Actions",
      cell: (r) => (
        <div style={{ display: "flex", gap: 8, whiteSpace: "nowrap" }}>
          <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => openDetail(r)}>View</button>
          <button className="w-btn w-btn-ghost w-btn-sm" title="Manually create today's order (use if the scheduler missed it, or to force another one)" onClick={() => createNow(r)}>Create now</button>
          <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => openEdit(r)}>Edit</button>
          <button className="w-btn w-btn-sm w-btn-danger" onClick={() => remove(r)}>Delete</button>
        </div>
      ),
      width: "330px",
    },
  ];

  // Run-history columns for a task's detail view.
  const runColumns = [
    { name: "Date", selector: (r) => r.runDate || "—", width: "110px" },
    { name: "Time", selector: (r) => (r.runAt ? r.runAt.slice(11, 16) : "—"), width: "80px" },
    { name: "Trigger", selector: (r) => (r.triggeredBy === "MANUAL" ? "Manual" : "Scheduled"), width: "110px" },
    {
      name: "Status",
      cell: (r) => <Pill tone={r.status === "OK" ? "success" : "danger"}>{r.status === "OK" ? "Created" : "Failed"}</Pill>,
      width: "110px",
    },
    { name: "Reference", selector: (r) => r.reference || "—", grow: 1.2 },
    { name: "Details", selector: (r) => r.message || "—", grow: 2 },
  ];

  // Fix-and-review grid for the Excel import. Cells hold the values that WILL be saved (not the
  // raw sheet cells) and the simple ones are editable in place; driver / partner / addresses are
  // edited in the expanded row, where a searchable dropdown is affordable.
  const importCell = (r, field, opts = {}) => (
    <CellInput
      value={r.payload[field] ?? ""}
      type={opts.type}
      list={opts.list}
      invalid={r.errorFields.has(field)}
      onCommit={(v) => editRow(r.idx, field, v)}
    />
  );

  const importColumns = [
    {
      name: "Row",
      cell: (r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "#9ca3af", fontSize: 12 }}>{r.idx + 2}</span>
          <button
            className="w-btn w-btn-ghost w-btn-sm"
            style={{ padding: "2px 6px", fontSize: 11 }}
            title={r.excluded ? "Include this row again" : "Leave this row out of the import"}
            onClick={() => toggleExcluded(r.idx)}
          >
            {r.excluded ? "undo" : "skip"}
          </button>
        </div>
      ),
      width: "110px",
    },
    {
      name: "Status",
      cell: (r) =>
        r.excluded ? (
          <Pill tone="neutral">Skipped</Pill>
        ) : r.errors.length > 0 ? (
          <Pill tone="danger">Fix</Pill>
        ) : r.warnings.length > 0 ? (
          <Pill tone="warning">Check</Pill>
        ) : (
          <Pill tone="success">Ready</Pill>
        ),
      width: "95px",
    },
    { name: "Task name", cell: (r) => importCell(r, "name"), width: "220px" },
    ...(isMedical
      ? [
          {
            name: "Driver",
            cell: (r) => {
              const label = driverOptions.find((o) => o.value === r.payload.driverId)?.label;
              return label ? (
                <span style={{ fontSize: 12.5 }} title={`From file: ${r.sheetDriver || "—"}`}>
                  {label}
                  {r.driverHow === "partial" && !r.driverEdited && <span style={{ color: "#b45309" }} title="matched by partial name"> ≈</span>}
                </span>
              ) : (
                <span style={{ fontSize: 12.5, color: "#dc2626" }} title="Open the row (›) or use the driver list above">
                  {r.sheetDriver || "no driver"}
                </span>
              );
            },
            width: "190px",
          },
          { name: "Trip", cell: (r) => importCell(r, "tripNumber", { type: "number" }), width: "80px" },
          {
            name: "Task type",
            cell: (r) => (
              <CellSelect
                value={r.payload.taskType || ""}
                invalid={r.errorFields.has("taskType")}
                onChange={(v) => editRow(r.idx, "taskType", v)}
                options={TASK_TYPE_OPTIONS}
              />
            ),
            width: "150px",
          },
          { name: "Team", cell: (r) => importCell(r, "teamId"), width: "100px" },
          { name: "Homebase (drop)", cell: (r) => importCell(r, "dropHomebaseId", { list: "rt-homebase-list" }), width: "170px" },
          { name: "Location (pickup)", cell: (r) => importCell(r, "pickupLocationId", { list: "rt-location-list" }), width: "180px" },
          { name: "Sender city", cell: (r) => importCell(r, "pickupCity"), width: "130px" },
          { name: "Consignee city", cell: (r) => importCell(r, "deliveryCity"), width: "140px" },
          { name: "Drop start", cell: (r) => importCell(r, "dropStartTime", { type: "time" }), width: "120px" },
          { name: "Drop end", cell: (r) => importCell(r, "dropEndTime", { type: "time" }), width: "120px" },
        ]
      : [
          { name: "Receiver", cell: (r) => importCell(r, "receiverName"), width: "180px" },
          { name: "Phone", cell: (r) => importCell(r, "receiverPhone"), width: "150px" },
          { name: "Address", cell: (r) => importCell(r, "deliveryAddress"), width: "240px" },
          { name: "City", cell: (r) => importCell(r, "deliveryCity"), width: "130px" },
          { name: "COD", cell: (r) => importCell(r, "codAmount"), width: "110px" },
          { name: "Weight", cell: (r) => importCell(r, "weightKg", { type: "number" }), width: "100px" },
          { name: "Items", cell: (r) => importCell(r, "noItems", { type: "number" }), width: "90px" },
        ]),
    { name: "Runs at", cell: (r) => importCell(r, "runTime", { type: "time" }), width: "120px" },
    {
      name: "Issues",
      cell: (r) => (
        <div style={{ fontSize: 11.5, padding: "6px 0", lineHeight: 1.45 }}>
          {r.errors.map((e, i) => (
            <div key={`e${i}`} style={{ color: "#dc2626" }}>• {e}</div>
          ))}
          {r.warnings.map((w, i) => (
            <div key={`w${i}`} style={{ color: "#b45309" }}>• {w}</div>
          ))}
          {r.notes.map((nt, i) => (
            <div key={`n${i}`} style={{ color: "#6b7280" }}>• {nt}</div>
          ))}
          {r.errors.length === 0 && r.warnings.length === 0 && r.notes.length === 0 && (
            <span style={{ color: "#16a34a" }}>OK</span>
          )}
        </div>
      ),
      width: "280px",
      wrap: true,
    },
  ];

  const tableStyles = {
    headRow: { style: { backgroundColor: "#fafbfc", borderBottom: "1px solid #e7eaf0", minHeight: "46px" } },
    headCells: { style: { fontWeight: "700", fontSize: "11.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: ".4px" } },
    rows: { style: { minHeight: "52px", fontSize: "13.5px" } },
  };

  // ------------------------------------------------------------------ render
  return (
    <div className="wallet-page">
      <div className="w-topbar">
        <div className="w-topbar-left">
          <div>
            <h2 className="w-title">Regular Task — {label}</h2>
            <p className="w-subtitle">Set up a trip once — a {label.toLowerCase()} order is created automatically every day at the set time.</p>
          </div>
        </div>
      </div>

      {banner && <div className={`w-banner ${banner.type === "success" ? "w-banner-success" : "w-banner-error"}`}>{banner.text}</div>}

      {mode === "list" && (
        <div className="w-card">
          <div className="w-card-header">
            <span className="w-card-title">{label} regular tasks</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ display: "flex", border: "1px solid #e7eaf0", borderRadius: 8, overflow: "hidden" }}>
                <button
                  onClick={() => setViewMode("table")}
                  style={{
                    padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer",
                    background: viewMode === "table" ? "#00a6ed" : "#fff", color: viewMode === "table" ? "#fff" : "#6b7280",
                  }}
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode("card")}
                  style={{
                    padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer",
                    background: viewMode === "card" ? "#00a6ed" : "#fff", color: viewMode === "card" ? "#fff" : "#6b7280",
                  }}
                >
                  Cards
                </button>
              </div>
              <button className="w-btn w-btn-ghost w-btn-sm" onClick={fetchTasks}>Refresh</button>
              <button className="w-btn w-btn-ghost w-btn-sm" onClick={openImport} title="Create many trips at once from an Excel/CSV file">
                Import Excel
              </button>
              <button className="w-btn w-btn-primary w-btn-sm" onClick={openCreate}>New {label} Trip</button>
            </div>
          </div>
          <div className="w-card-body">
            {/* ---- search / filter toolbar ---- */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
                <i className="fa fa-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 12 }} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search task, partner, driver, trip, city, time…"
                  style={{ width: "100%", padding: "7px 28px 7px 30px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, outline: "none" }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    title="Clear search"
                    style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 14 }}
                  >
                    ×
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[["ALL", "All"], ["ENABLED", "Enabled"], ["DISABLED", "Disabled"]].map(([v, lbl]) => (
                  <button
                    key={v}
                    onClick={() => setStatusFilter(v)}
                    className="w-btn w-btn-sm"
                    style={{
                      background: statusFilter === v ? "#00a6ed" : "#fff",
                      color: statusFilter === v ? "#fff" : "#6b7280",
                      border: "1px solid #d1d5db",
                    }}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                {visibleRows.length} of {rows.length} trip{rows.length === 1 ? "" : "s"}
              </span>
            </div>

            {/* ---- bulk action bar (table view only — selection lives on the table) ---- */}
            {viewMode === "table" && selectedRows.length > 0 && (
              <div
                style={{
                  display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                  marginBottom: 12, padding: "8px 12px", borderRadius: 8,
                  background: "#eff6ff", border: "1px solid #bfdbfe",
                }}
              >
                <strong style={{ fontSize: 13, color: "#1e40af" }}>{selectedRows.length} selected</strong>
                <button className="w-btn w-btn-ghost w-btn-sm" disabled={bulkBusy} onClick={() => runBulk("enable")}>Enable</button>
                <button className="w-btn w-btn-ghost w-btn-sm" disabled={bulkBusy} onClick={() => runBulk("disable")}>Disable</button>
                <button className="w-btn w-btn-ghost w-btn-sm" disabled={bulkBusy} onClick={() => runBulk("run")} title="Manually create today's order for each selected trip">Create now</button>
                <button className="w-btn w-btn-sm w-btn-danger" disabled={bulkBusy} onClick={() => runBulk("delete")}>Delete</button>
                <button
                  className="w-btn w-btn-ghost w-btn-sm"
                  disabled={bulkBusy}
                  onClick={() => { setSelectedRows([]); setClearSelectionToggle((t) => !t); }}
                >
                  Clear
                </button>
                {bulkBusy && <span style={{ fontSize: 12, color: "#1e40af" }}>Working…</span>}
              </div>
            )}

            {viewMode === "table" ? (
              <DataTable
                columns={columns}
                data={visibleRows}
                keyField="id"
                progressPending={loading}
                pagination
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                dense
                selectableRows
                onSelectedRowsChange={({ selectedRows: sel }) => setSelectedRows(sel)}
                clearSelectedRows={clearSelectionToggle}
                customStyles={tableStyles}
                noDataComponent={
                  <div style={{ padding: 24, color: "#64748b" }}>
                    {rows.length === 0
                      ? `No ${label.toLowerCase()} trips yet — click “New ${label} Trip”.`
                      : "No trips match your search / filter."}
                  </div>
                }
              />
            ) : loading ? (
              <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>Loading…</div>
            ) : visibleRows.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
                {rows.length === 0
                  ? `No ${label.toLowerCase()} trips yet — click “New ${label} Trip”.`
                  : "No trips match your search / filter."}
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 14 }}>
                {visibleRows.map((r) => {
                  const failed = r.lastRunStatus && String(r.lastRunStatus).startsWith("FAILED");
                  return (
                    <div
                      key={r.id}
                      onClick={() => openDetail(r)}
                      style={{
                        border: "1px solid #e7eaf0", borderRadius: 12, padding: 16, cursor: "pointer",
                        background: "#fff", transition: "box-shadow .15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,24,40,.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 14.5, color: "#111827" }}>{r.name}</div>
                        <ToggleSwitch checked={r.enabled} onChange={() => toggleEnabled(r)} showLabel={false} />
                      </div>
                      <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 6 }}>
                        {r.partnerName || (r.partnerId ? `#${r.partnerId}` : "No partner")} · Trip {r.tripNumber ?? "—"}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 2 }}>
                        Runs at {(r.runTime || "00:00").slice(0, 5)} ({r.timezone || "Asia/Dubai"})
                      </div>
                      <div style={{ fontSize: 12, marginTop: 10, color: r.lastRunAt ? (failed ? "#dc2626" : "#16a34a") : "#9ca3af" }}>
                        {r.lastRunAt ? `Last run ${r.lastRunAt} — ${failed ? "failed" : "ok"}` : "Never run yet"}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                        <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => createNow(r)}>Create now</button>
                        <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => openEdit(r)}>Edit</button>
                        <button className="w-btn w-btn-sm w-btn-danger" onClick={() => remove(r)}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {mode === "detail" && detailRow && (
        <>
          <div className="w-card-header" style={{ background: "transparent", border: "none", padding: "0 0 14px" }}>
            <span className="w-card-title" style={{ fontSize: 17 }}>{detailRow.name}</span>
            <button className="w-crumb" onClick={closeDetail}>Back to list</button>
          </div>

          <div className="w-card">
            <div className="w-card-body" style={{ paddingTop: 16, display: "flex", flexWrap: "wrap", gap: 28 }}>
              <div>
                <div className="w-field-label">Partner</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{detailRow.partnerName || (detailRow.partnerId ? `#${detailRow.partnerId}` : "—")}</div>
              </div>
              <div>
                <div className="w-field-label">Trip #</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{detailRow.tripNumber ?? "—"}</div>
              </div>
              <div>
                <div className="w-field-label">Runs at</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{(detailRow.runTime || "00:00").slice(0, 5)} ({detailRow.timezone || "Asia/Dubai"})</div>
              </div>
              <div>
                <div className="w-field-label">Status</div>
                <div style={{ marginTop: 4 }}>
                  <ToggleSwitch
                    checked={detailRow.enabled}
                    onChange={() => { toggleEnabled(detailRow); setDetailRow((d) => ({ ...d, enabled: !d.enabled })); }}
                  />
                </div>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignSelf: "center" }}>
                <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => openEdit(detailRow)}>Edit trip</button>
                <button className="w-btn w-btn-primary w-btn-sm" onClick={() => createNow(detailRow)}>Create now</button>
              </div>
            </div>
          </div>

          <div className="w-card">
            <div className="w-card-header">
              <span className="w-card-title">Orders created by this task</span>
              <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => fetchRuns(detailRow.id)}>Refresh</button>
            </div>
            <div className="w-card-body">
              <DataTable
                columns={runColumns}
                data={runs}
                progressPending={runsLoading}
                pagination
                highlightOnHover
                dense
                customStyles={tableStyles}
                noDataComponent={<div style={{ padding: 24, color: "#64748b" }}>No orders created yet — this task hasn't fired (or hasn't been triggered manually).</div>}
              />
            </div>
          </div>
        </>
      )}

      {mode === "import" && (
        <>
          <div className="w-card-header" style={{ background: "transparent", border: "none", padding: "0 0 14px" }}>
            <span className="w-card-title" style={{ fontSize: 17 }}>Import {label.toLowerCase()} trips from Excel</span>
            <button className="w-crumb" onClick={closeImport}>Back to list</button>
          </div>

          <Stepper
            step={importStep}
            onGo={setImportStep}
            steps={[
              { title: "Upload file" },
              { title: "Partner & schedule", reachable: importParsed.length > 0 },
              { title: "Fix & review", reachable: importParsed.length > 0 },
              { title: "Create", reachable: importParsed.length > 0 },
            ]}
          />

          {/* -------------------------------------------------- step 1: file */}
          {importStep === 1 && (
            <>
              <div className="w-banner" style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }}>
                Each row becomes one <b>regular</b> {label.toLowerCase()} task — it creates a new order <b>every day</b> at its run
                time until you disable it. <b>Start Date / End Date are ignored</b> (a regular task has no date range) and so is any
                Reference column — every daily order gets its own reference automatically.
              </div>

              <div className="w-card">
                <div className="w-card-body" style={{ paddingTop: 16 }}>
                  <SectionHeading
                    step={1}
                    title="Choose the file"
                    desc={`Your existing ${label.toLowerCase()} bulk-upload sheet works as-is — same columns, same names.`}
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                    <label className="w-btn w-btn-primary" style={{ cursor: "pointer" }}>
                      {importFile ? "Choose a different file" : "Select .xlsx / .xls / .csv"}
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          parseFile(f || null);
                          e.target.value = ""; // allow re-selecting the same file after a fix
                        }}
                      />
                    </label>
                    <button className="w-btn w-btn-ghost" onClick={downloadTemplate}>Download template</button>
                    {importFile && (
                      <span style={{ fontSize: 13, color: "#374151" }}>
                        <b>{importFile.name}</b> — {importParsed.length} row(s)
                      </span>
                    )}
                  </div>
                  {ignoredPresent.length > 0 && (
                    <div className="w-help-text" style={{ marginTop: 10 }}>
                      Found and <b>ignored</b> in this file: {ignoredPresent.join(", ")}.
                    </div>
                  )}
                  <div className="w-help-text" style={{ marginTop: 10 }}>
                    Needed per row:{" "}
                    {isMedical
                      ? "Driver (name or id), Task Type, Team Id, Homebase Id (Drop off), Sender City, Consignee City, Drop Start/End Time"
                      : "Receiver's Name, Receiver's Mobile Number, Delivery Adress, City"}
                    . Partner can come from a Partner Name column or be chosen in the next step. Anything missing can be filled in
                    on screen in step 3.
                  </div>
                </div>
              </div>
            </>
          )}

          {/* -------------------------------------------------- step 2: partner + schedule */}
          {importStep === 2 && importParsed.length > 0 && (
            <>
              <div className="w-card">
                <div className="w-card-body" style={{ paddingTop: 16 }}>
                  <SectionHeading step={2} title="Partner & schedule" desc="Applies to every row in the file." />
                  <div className="w-form-row">
                    <div className="w-field" style={{ flex: "1 1 320px" }}>
                      <label className="w-field-label">
                        Partner {!sheetHasPartnerColumn && <span style={{ color: "#dc2626" }}>*</span>}
                      </label>
                      <Select
                        options={partnerOptions}
                        value={importPartner}
                        onChange={setImportPartner}
                        placeholder="Select partner…"
                        isClearable
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                      />
                      <div className="w-help-text" style={{ marginTop: 4 }}>
                        {sheetHasPartnerColumn
                          ? "This file has a Partner column — this is only used for rows whose partner can't be matched."
                          : "This file has no Partner column, so every row uses this partner (changeable per row in step 3)."}
                      </div>
                    </div>
                    <Field label="Create order at" hint="Each day, in the timezone below. A Run Time column overrides it.">
                      <input
                        type="time"
                        className="w-input"
                        value={importDefaults.runTime}
                        onChange={(e) => setImportDefaults((d) => ({ ...d, runTime: e.target.value }))}
                      />
                    </Field>
                    <Field label="Timezone">
                      <input
                        className="w-input"
                        value={importDefaults.timezone}
                        onChange={(e) => setImportDefaults((d) => ({ ...d, timezone: e.target.value }))}
                      />
                    </Field>
                    <Field label="Options">
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={importDefaults.enabled !== false}
                            onChange={(e) => setImportDefaults((d) => ({ ...d, enabled: e.target.checked }))}
                          />
                          Start running daily right away
                        </label>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={importDefaults.skipDuplicates}
                            onChange={(e) => setImportDefaults((d) => ({ ...d, skipDuplicates: e.target.checked }))}
                          />
                          Skip names that already exist
                        </label>
                      </div>
                    </Field>
                  </div>
                </div>
              </div>

              {/* what each column in THIS file was understood as */}
              <div className="w-card">
                <div className="w-card-header">
                  <span className="w-card-title">Columns detected in {importFile?.name || "this file"}</span>
                </div>
                <div className="w-card-body">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {columnMap.map((c) => (
                      <span
                        key={c.header}
                        title={c.note}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "5px 10px",
                          borderRadius: 8,
                          fontSize: 12,
                          border: "1px solid",
                          borderColor: c.status === "mapped" ? "#bbf7d0" : c.status === "ignored" ? "#e5e7eb" : "#fed7aa",
                          background: c.status === "mapped" ? "#f0fdf4" : c.status === "ignored" ? "#f9fafb" : "#fff7ed",
                          color: c.status === "ignored" ? "#6b7280" : "#111827",
                        }}
                      >
                        <b>{c.header}</b>
                        <span style={{ color: "#6b7280" }}>→ {c.target}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="w-card">
                <div className="w-card-body" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <button className="w-btn w-btn-ghost" onClick={() => setImportStep(1)}>Back</button>
                  <button
                    className="w-btn w-btn-primary"
                    onClick={() => setImportStep(3)}
                    disabled={!sheetHasPartnerColumn && !importPartner}
                  >
                    Continue — check {importParsed.length} row(s)
                  </button>
                </div>
              </div>
            </>
          )}

          {/* -------------------------------------------------- step 3: fix & review */}
          {importStep === 3 && importParsed.length > 0 && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                {[
                  { label: "Ready", value: importReady.length, tone: "#16a34a", bg: "#f0fdf4" },
                  { label: "Need fixing", value: importBlocked.length, tone: "#dc2626", bg: "#fef2f2" },
                  { label: "Warnings", value: importRows.filter((r) => !r.excluded && r.errors.length === 0 && r.warnings.length > 0).length, tone: "#b45309", bg: "#fffbeb" },
                  { label: "Skipped", value: importRows.filter((r) => r.excluded).length, tone: "#6b7280", bg: "#f9fafb" },
                ].map((c) => (
                  <div key={c.label} style={{ padding: "10px 16px", borderRadius: 10, background: c.bg, border: "1px solid #e7eaf0", minWidth: 120 }}>
                    <div style={{ fontSize: 11.5, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".4px" }}>{c.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: c.tone }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {/* driver name -> driver, one entry per distinct name in the file */}
              {isMedical && driverGroups.length > 0 && (
                <div className="w-card">
                  <div className="w-card-header">
                    <span className="w-card-title">Drivers in this file ({driverGroups.length})</span>
                    <span className="w-help-text">Changing one here updates every row that uses that name.</span>
                  </div>
                  <div className="w-card-body">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 14 }}>
                      {driverGroups.map((g) => {
                        const current = driverOptions.find((o) => o.value === g.driverId) || null;
                        return (
                          <div key={g.key} style={{ border: "1px solid", borderColor: current ? "#e7eaf0" : "#fecaca", borderRadius: 10, padding: 12, background: current ? "#fff" : "#fef2f2" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
                              <b style={{ fontSize: 13 }}>{g.display}</b>
                              <span style={{ fontSize: 11.5, color: "#6b7280" }}>{g.rows.length} row(s)</span>
                            </div>
                            <Select
                              options={driverOptions}
                              value={g.mixed ? null : current}
                              onChange={(opt) => editMany("driverId", opt ? opt.value : "", (r) => (norm(r.sheetDriver) || `__row${r.idx}`) === g.key)}
                              placeholder={g.mixed ? "Mixed — pick one for all" : "Search driver…"}
                              isClearable
                              isSearchable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={selectStyles}
                              noOptionsMessage={() => "No drivers found"}
                            />
                            {current && g.how === "partial" && !g.edited && (
                              <div className="w-help-text" style={{ marginTop: 4, color: "#b45309" }}>Matched by partial name — confirm it's right.</div>
                            )}
                            {!current && <div className="w-help-text" style={{ marginTop: 4, color: "#dc2626" }}>Not found — pick the driver.</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* partner name -> partner (only when the sheet carries partner names) */}
              {partnerGroups.length > 0 && (
                <div className="w-card">
                  <div className="w-card-header">
                    <span className="w-card-title">Partners in this file ({partnerGroups.length})</span>
                  </div>
                  <div className="w-card-body">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 14 }}>
                      {partnerGroups.map((g) => (
                        <div key={g.key}>
                          <label className="w-field-label">{g.display} <span style={{ fontWeight: 400, color: "#6b7280" }}>· {g.rows.length} row(s)</span></label>
                          <Select
                            options={partnerOptions}
                            value={partnerOptions.find((o) => o.value === g.partnerId) || null}
                            onChange={(opt) => editMany("partnerId", opt ? opt.value : null, (r) => norm(r.sheetPartner) === g.key)}
                            placeholder="Select partner…"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={selectStyles}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* set one field on every row at once */}
              <div className="w-card">
                <div className="w-card-body" style={{ paddingTop: 16 }}>
                  <SectionHeading step={3} title="Fix the rows" desc="Edit any cell in the table below, or set one field on every row here." />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
                    <div className="w-field" style={{ flex: "0 1 220px" }}>
                      <label className="w-field-label">Set for every row</label>
                      <select
                        className="w-input"
                        value={bulkField}
                        onChange={(e) => { setBulkField(e.target.value); setBulkValue(""); }}
                      >
                        {bulkFields.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="w-field" style={{ flex: "0 1 220px" }}>
                      <label className="w-field-label">Value</label>
                      {bulkFieldDef.type === "select" ? (
                        <select className="w-input" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                          <option value="">Select…</option>
                          {TASK_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="w-input"
                          type={bulkFieldDef.type === "number" ? "number" : bulkFieldDef.type}
                          list={bulkFieldDef.list}
                          value={bulkValue}
                          onChange={(e) => setBulkValue(e.target.value)}
                        />
                      )}
                    </div>
                    <button
                      className="w-btn w-btn-ghost"
                      disabled={bulkValue === ""}
                      onClick={() => { editMany(bulkField, bulkValue); flash("success", `${bulkFieldDef.label} set on all ${importRows.length} row(s).`); }}
                    >
                      Apply to all rows
                    </button>
                    <button
                      className="w-btn w-btn-ghost"
                      disabled={bulkValue === "" || importBlocked.length === 0}
                      onClick={() => { editMany(bulkField, bulkValue, (r) => r.errors.length > 0); flash("success", `${bulkFieldDef.label} set on ${importBlocked.length} row(s) needing fixes.`); }}
                    >
                      Apply to rows needing fixes
                    </button>
                    {Object.keys(rowEdits).length > 0 && (
                      <button className="w-btn w-btn-ghost" onClick={clearEdits}>Undo all my edits</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-card">
                <div className="w-card-header">
                  <span className="w-card-title">Rows ({importRows.length})</span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#6b7280" }}>
                      <input type="checkbox" checked={showOnlyIssues} onChange={(e) => setShowOnlyIssues(e.target.checked)} />
                      Only rows needing attention
                    </label>
                    {importBlocked.length > 0 && (
                      <button className="w-btn w-btn-ghost w-btn-sm" onClick={downloadInvalidRows}>Download rows to fix</button>
                    )}
                  </div>
                </div>
                <div className="w-card-body">
                  <div className="w-help-text" style={{ marginBottom: 10 }}>
                    Type straight into a cell (it saves when you leave the cell). Open a row with <b>›</b> to change its driver,
                    partner or addresses. Homebase and Location are sent to the order exactly as written — the same as the medical
                    bulk upload.
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <DataTable
                      columns={importColumns}
                      data={showOnlyIssues ? importRows.filter((r) => r.errors.length > 0 || r.warnings.length > 0) : importRows}
                      keyField="idx"
                      pagination
                      paginationPerPage={20}
                      paginationRowsPerPageOptions={[10, 20, 50, 100]}
                      dense
                      customStyles={tableStyles}
                      expandableRows
                      expandableRowsComponent={ImportRowEditor}
                      expandableRowsComponentProps={{ isMedical, partnerOptions, driverOptions, onEdit: editRow }}
                      conditionalRowStyles={[
                        { when: (r) => r.excluded, style: { opacity: 0.45 } },
                        { when: (r) => !r.excluded && r.errors.length > 0, style: { backgroundColor: "#fef2f2" } },
                        { when: (r) => !r.excluded && r.errors.length === 0 && r.warnings.length > 0, style: { backgroundColor: "#fffbeb" } },
                      ]}
                      noDataComponent={<div style={{ padding: 24, color: "#64748b" }}>Nothing to show.</div>}
                    />
                  </div>
                </div>
              </div>

              <div className="w-card">
                <div className="w-card-body" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <button className="w-btn w-btn-ghost" onClick={() => setImportStep(2)}>Back</button>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    {importBlocked.length > 0 && (
                      <span style={{ fontSize: 12.5, color: "#dc2626" }}>{importBlocked.length} row(s) still need fixing — they won't be created.</span>
                    )}
                    <button className="w-btn w-btn-primary" onClick={() => setImportStep(4)} disabled={importReady.length === 0}>
                      Continue with {importReady.length} row(s)
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* -------------------------------------------------- step 4: create */}
          {importStep === 4 && importParsed.length > 0 && (
            <>
              {!importResults && (
                <div className="w-card">
                  <div className="w-card-body" style={{ paddingTop: 16 }}>
                    <SectionHeading step={4} title="Create the regular tasks" desc="Last check before anything is saved." />
                    <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginBottom: 16 }}>
                      <div>
                        <div className="w-field-label">Tasks to create</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "#16a34a" }}>{importReady.length}</div>
                      </div>
                      <div>
                        <div className="w-field-label">Skipped</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "#6b7280" }}>{importSkipped}</div>
                      </div>
                      <div>
                        <div className="w-field-label">Runs at</div>
                        <div style={{ fontSize: 15, marginTop: 6 }}>{importDefaults.runTime} ({importDefaults.timezone})</div>
                      </div>
                      <div>
                        <div className="w-field-label">Status on create</div>
                        <div style={{ fontSize: 15, marginTop: 6 }}>{importDefaults.enabled !== false ? "Enabled — starts tomorrow" : "Disabled"}</div>
                      </div>
                    </div>
                    <div className="w-banner" style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" }}>
                      Each task creates a new {label.toLowerCase()} order <b>every day</b> at its run time until it's disabled.
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 16 }}>
                      <button className="w-btn w-btn-ghost" onClick={() => setImportStep(3)} disabled={importing}>Back</button>
                      <button className="w-btn w-btn-primary" onClick={submitImport} disabled={importing || importReady.length === 0}>
                        {importing ? `Creating ${importProgress.done}/${importProgress.total}…` : `Create ${importReady.length} regular task(s)`}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {importResults && (
                <div className="w-card">
                  <div className="w-card-header">
                    <span className="w-card-title">Import results</span>
                    {importResults.failed.length > 0 && (
                      <button className="w-btn w-btn-ghost w-btn-sm" onClick={downloadFailedRows}>Download failed rows</button>
                    )}
                  </div>
                  <div className="w-card-body">
                    <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginBottom: 14 }}>
                      <div>
                        <div className="w-field-label">Created</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#16a34a" }}>{importResults.created.length}</div>
                      </div>
                      <div>
                        <div className="w-field-label">Failed</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: importResults.failed.length ? "#dc2626" : "#111827" }}>
                          {importResults.failed.length}
                        </div>
                      </div>
                      <div>
                        <div className="w-field-label">Skipped</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#6b7280" }}>{importResults.skipped}</div>
                      </div>
                    </div>
                    {importResults.failed.length > 0 && (
                      <div style={{ fontSize: 12.5 }}>
                        {importResults.failed.map((f) => (
                          <div key={f.idx} style={{ padding: "6px 0", borderBottom: "1px solid #f1f5f9" }}>
                            <b>Row {f.idx + 2}</b> — {f.payload.name}: <span style={{ color: "#dc2626" }}>{f.error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: 16 }}>
                      <button className="w-btn w-btn-primary" onClick={closeImport}>Done — back to list</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* suggestion lists for the Homebase / Location cells */}
          <datalist id="rt-homebase-list">
            {homebases.map((h) => (
              <option key={h.homebaseCode} value={h.homebaseName}>{h.homebaseCode}</option>
            ))}
          </datalist>
          <datalist id="rt-location-list">
            {importWarehouses.map((w) => (
              <option key={w.id} value={w.name}>{w.id}</option>
            ))}
          </datalist>
        </>
      )}

      {mode === "form" && (
        <>
          <div className="w-card-header" style={{ background: "transparent", border: "none", padding: "0 0 14px" }}>
            <span className="w-card-title" style={{ fontSize: 17 }}>{form.id ? "Edit" : "New"} {label} trip</span>
            <button className="w-crumb" onClick={closeForm}>Back to list</button>
          </div>

          {form.id && (
            <div className="w-banner" style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }}>
              Editing applies to the <b>next</b> scheduled order — today's order (if it was already created) is unchanged.
            </div>
          )}

          <div className="w-help-text" style={{ marginBottom: 12 }}>
            Fields marked <span style={{ color: "#dc2626", fontWeight: 700 }}>*</span> are required.
          </div>

          {/* Trip basics */}
          <div className="w-card">
            <div className="w-card-body" style={{ paddingTop: 16 }}>
              <SectionHeading step={1} title="Trip" />
              <div className="w-form-row">
                <div className="w-field" style={{ flex: "1 1 280px" }}>
                  <label className="w-field-label">Partner <span style={{ color: "#dc2626" }}>*</span></label>
                  <Select
                    options={partnerOptions}
                    value={form.partner}
                    onChange={onPartnerChange}
                    placeholder="Select partner…"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                  />
                  {isMedical && partnerOptions.length === 0 && (
                    <div className="w-help-text" style={{ marginTop: 4 }}>No medical partners found.</div>
                  )}
                </div>
                <Field label="Trip #" hint="Auto-numbered per partner.">
                  <input type="number" className="w-input" value={form.tripNumber} onChange={onTripChange} placeholder="1" />
                </Field>
                <Field label="Task name" required hint="Suggested from partner + trip; edit if you like.">
                  <input className="w-input" value={form.name} onChange={onNameChange} placeholder="e.g. Acme Labs - Trip 1" />
                </Field>
                <Field label="Create order at" hint="Time each day (default midnight).">
                  <input type="time" className="w-input" value={form.runTime} onChange={set("runTime")} />
                </Field>
                <Field label="Status">
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <input type="checkbox" checked={form.enabled !== false} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} /> Runs daily
                  </label>
                </Field>
              </div>
            </div>
          </div>

          {/* Assignment (medical only) */}
          {isMedical && (
            <div className="w-card">
              <div className="w-card-body" style={{ paddingTop: 16 }}>
                <SectionHeading step={2} title="Assignment" />
                <div className="w-form-row">
                  <div className="w-field" style={{ flex: "1 1 220px" }}>
                    <label className="w-field-label">Driver <span style={{ color: "#dc2626" }}>*</span></label>
                    <Select
                      options={driverOptions}
                      value={selectedDriver}
                      onChange={onDriverChange}
                      placeholder="Search driver…"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectStyles}
                      noOptionsMessage={() => "No drivers found"}
                    />
                  </div>
                  <Field label="Task type" required hint="A medical trip creates both a pickup and a delivery leg.">
                    <select className="w-input" value={form.taskType} onChange={set("taskType")}>
                      <option value="">Select…</option>
                      <option value="pickup_delivery">Pickup &amp; Delivery</option>
                      <option value="pickup">Pickup</option>
                      <option value="delivery">Delivery</option>
                    </select>
                  </Field>
                  <Field label="Team ID"><input className="w-input" value={form.teamId} onChange={set("teamId")} /></Field>
                </div>
              </div>
            </div>
          )}

          {/* Pickup */}
          <div className="w-card">
            <div className="w-card-body" style={{ paddingTop: 16 }}>
              <SectionHeading step={isMedical ? 3 : 2} title="Pickup" />
              <div className="w-form-row">
                {isMedical ? (
                  <>
                    <Field label="Pickup location (optional)" hint={!form.partner ? "Select a partner first." : warehouses.length ? "Optional — or just fill the pickup address below." : "No saved location for this partner — fill the pickup address below."}>
                      <select className="w-input" value={form.pickupLocationId} onChange={set("pickupLocationId")} disabled={!form.partner}>
                        <option value="">
                          {!form.partner ? "Select a partner first" : warehouses.length ? "Select location… (optional)" : "None — use pickup address below"}
                        </option>
                        {warehouses.map((w) => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Pickup name"><input className="w-input" value={form.pickupName} onChange={set("pickupName")} placeholder="e.g. Collection Point A" /></Field>
                    <Field label="Pickup phone"><input className="w-input" value={form.pickupPhone} onChange={set("pickupPhone")} /></Field>
                    <Field label="Sender city"><input className="w-input" value={form.pickupCity} onChange={set("pickupCity")} placeholder="e.g. Dubai" /></Field>
                    <div className="w-field" style={{ flex: "1 1 100%" }}>
                      <label className="w-field-label">Pickup address</label>
                      <textarea className="w-textarea" rows={2} value={form.pickupAddress} onChange={set("pickupAddress")} placeholder="e.g. Sample Collection Center, Building 12, Al Barsha" />
                    </div>
                  </>
                ) : (
                  <>
                    <Field label="Pickup name"><input className="w-input" value={form.pickupName} onChange={set("pickupName")} /></Field>
                    <Field label="Pickup city"><input className="w-input" value={form.pickupCity} onChange={set("pickupCity")} /></Field>
                    <div className="w-field" style={{ flex: "1 1 100%" }}>
                      <label className="w-field-label">Pickup address</label>
                      <textarea className="w-textarea" rows={2} value={form.pickupAddress} onChange={set("pickupAddress")} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Drop */}
          <div className="w-card">
            <div className="w-card-body" style={{ paddingTop: 16 }}>
              <SectionHeading step={isMedical ? 4 : 3} title="Drop" />
              <div className="w-form-row">
                {isMedical ? (
                  <>
                    <Field label="Drop homebase">
                      <select className="w-input" value={form.dropHomebaseId} onChange={set("dropHomebaseId")}>
                        <option value="">Select homebase…</option>
                        {homebases.map((h) => (
                          <option key={h.homebaseCode} value={h.homebaseCode}>{h.homebaseName}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Receiver name"><input className="w-input" value={form.receiverName} onChange={set("receiverName")} placeholder="e.g. Dubai Hospital Lab" /></Field>
                    <Field label="Receiver phone"><input className="w-input" value={form.receiverPhone} onChange={set("receiverPhone")} /></Field>
                    <Field label="Consignee city"><input className="w-input" value={form.deliveryCity} onChange={set("deliveryCity")} placeholder="e.g. Abu Dhabi" /></Field>
                    <Field label="Drop start time"><input type="time" className="w-input" value={form.dropStartTime} onChange={set("dropStartTime")} /></Field>
                    <Field label="Drop end time"><input type="time" className="w-input" value={form.dropEndTime} onChange={set("dropEndTime")} /></Field>
                    <div className="w-field" style={{ flex: "1 1 100%" }}>
                      <label className="w-field-label">Delivery address</label>
                      <textarea className="w-textarea" rows={2} value={form.deliveryAddress} onChange={set("deliveryAddress")} placeholder="e.g. Building 64, Healthcare City, Dubai" />
                    </div>
                  </>
                ) : (
                  <>
                    <Field label="Receiver name"><input className="w-input" value={form.receiverName} onChange={set("receiverName")} /></Field>
                    <Field label="Receiver phone"><input className="w-input" value={form.receiverPhone} onChange={set("receiverPhone")} /></Field>
                    <Field label="Apt / details"><input className="w-input" value={form.deliveryAddressDetail} onChange={set("deliveryAddressDetail")} /></Field>
                    <Field label="City"><input className="w-input" value={form.deliveryCity} onChange={set("deliveryCity")} /></Field>
                    <div className="w-field" style={{ flex: "1 1 100%" }}>
                      <label className="w-field-label">Delivery address</label>
                      <textarea className="w-textarea" rows={2} value={form.deliveryAddress} onChange={set("deliveryAddress")} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Normal package */}
          {!isMedical && (
            <div className="w-card">
              <div className="w-card-body" style={{ paddingTop: 16 }}>
                <SectionHeading step={4} title="Package & payment" />
                <div className="w-form-row">
                  <Field label="Item description"><input className="w-input" value={form.itemDesc} onChange={set("itemDesc")} /></Field>
                  <Field label="COD amount" hint="Blank / 0 = no COD"><input className="w-input" value={form.codAmount} onChange={set("codAmount")} /></Field>
                  <Field label="Weight (kg)"><input type="number" step="any" className="w-input" value={form.weightKg} onChange={set("weightKg")} /></Field>
                  <Field label="No. of items"><input type="number" className="w-input" value={form.noItems} onChange={set("noItems")} /></Field>
                  <Field label="Slot"><input type="number" className="w-input" value={form.slot} onChange={set("slot")} /></Field>
                </div>
              </div>
            </div>
          )}

          <div className="w-card">
            <div className="w-card-body" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <button className="w-btn w-btn-ghost" onClick={resetAll}>Reset all</button>
              <div style={{ display: "flex", gap: 12 }}>
                <button className="w-btn w-btn-ghost" onClick={closeForm}>Cancel</button>
                <button className="w-btn w-btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : form.id ? "Save changes" : `Create ${label} trip`}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
